import React, { useState } from 'react';
import { Copy, Check, Download, Crown, Flag, Star, BadgeCheck } from 'lucide-react';
import { LOGO } from '../assets';

const NOISE_BG = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// Which lucide icon best represents a member's role badge
function tierIcon(roleNames) {
  const names = (roleNames || []).join(' ').toLowerCase();
  if (names.includes('coach')) return Crown;
  if (names.includes('run leader')) return Flag;
  return Star;
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawImageCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height, br = w / h;
  let sx, sy, sw, sh;
  if (ir > br) { sh = img.height; sw = sh * br; sx = (img.width - sw) / 2; sy = 0; }
  else { sw = img.width; sh = sw / br; sx = 0; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

// Draws centred on (cx, cy), scaled to fit inside maxW x maxH without stretching/squashing
function drawImageContain(ctx, img, cx, cy, maxW, maxH) {
  const ir = img.width / img.height;
  let w = maxW, h = maxW / ir;
  if (h > maxH) { h = maxH; w = maxH * ir; }
  ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Renders an "exclusive black-card VIP pass" style membership card to a PNG data URL
async function renderLicenseCard({ user, tier, memberSince, validThru }) {
  const W = 1000, H = 620, DPR = 2, R = 28;
  const canvas = document.createElement('canvas');
  canvas.width = W * DPR; canvas.height = H * DPR;
  const ctx = canvas.getContext('2d');
  ctx.scale(DPR, DPR);

  roundRectPath(ctx, 0, 0, W, H, R);
  ctx.save();
  ctx.clip();

  ctx.fillStyle = '#0a0a0a'; ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(50, 30, 10, 50, 30, 520);
  glow.addColorStop(0, 'rgba(225,226,10,.14)'); glow.addColorStop(1, 'rgba(225,226,10,0)');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  ctx.fillStyle = '#e1e20a';
  ctx.fillRect(0, 0, 10, H);

  // Faint giant rotated shield watermark — driving-licence style security texture
  try {
    const wmImg = await loadImage(LOGO);
    ctx.save();
    ctx.globalAlpha = 0.05;
    ctx.translate(W - 190, H - 120);
    ctx.rotate(-0.18);
    drawImageContain(ctx, wmImg, 0, 0, 420, 420);
    ctx.restore();
  } catch { /* watermark is decorative only */ }

  let logoDrawn = false;
  try {
    const logoImg = await loadImage(LOGO);
    roundRectPath(ctx, 40, 34, 46, 46, 12);
    ctx.save(); ctx.clip();
    drawImageCover(ctx, logoImg, 40, 34, 46, 46);
    ctx.restore();
    logoDrawn = true;
  } catch { /* fall back to text badge below */ }
  if (!logoDrawn) {
    roundRectPath(ctx, 40, 34, 46, 46, 12);
    ctx.fillStyle = '#e1e20a'; ctx.fill();
    ctx.fillStyle = '#0a0a0a'; ctx.font = '900 19px Arial, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('SPC', 63, 58);
  }

  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#fff'; ctx.font = '800 22px Arial, sans-serif';
  ctx.fillText('SPC CLUBHOUSE', 102, 52);
  ctx.fillStyle = '#8b8e93'; ctx.font = '600 11px Arial, sans-serif';
  ctx.fillText('OFFICIAL MEMBERSHIP CARD', 102, 71);

  const tierText = ((user?.roleNames && user.roleNames.length) ? user.roleNames.join(' · ') : (tier || 'SPC Club')).toUpperCase();
  ctx.font = '900 24px Arial, sans-serif';
  const tw = ctx.measureText(tierText).width;
  const pillW = tw + 56, pillH = 50, pillX = W - 40 - pillW, pillY = 28;
  roundRectPath(ctx, pillX, pillY, pillW, pillH, 25);
  ctx.fillStyle = 'rgba(225,226,10,.18)'; ctx.fill();
  roundRectPath(ctx, pillX, pillY, pillW, pillH, 25);
  ctx.strokeStyle = 'rgba(225,226,10,.7)'; ctx.lineWidth = 2; ctx.stroke();
  ctx.shadowColor = 'rgba(225,226,10,.6)'; ctx.shadowBlur = 20;
  ctx.fillStyle = '#e1e20a'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(tierText, pillX + pillW / 2, pillY + pillH / 2 + 1);
  ctx.shadowBlur = 0;
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.beginPath();
  ctx.moveTo(40, 102); ctx.lineTo(W - 40, 102); ctx.stroke();

  // Photo takes the large left column freed up by the removed QR code
  const photoX = 40, photoY = 112, photoSize = 340;
  roundRectPath(ctx, photoX, photoY, photoSize, photoSize, 22);
  ctx.fillStyle = '#151515'; ctx.fill();
  ctx.save();
  roundRectPath(ctx, photoX, photoY, photoSize, photoSize, 22); ctx.clip();
  if (user?.avatar) {
    try { drawImageCover(ctx, await loadImage(user.avatar), photoX, photoY, photoSize, photoSize); }
    catch { /* fall back to initials below */ }
  } else {
    ctx.fillStyle = '#e1e20a'; ctx.fillRect(photoX, photoY, photoSize, photoSize);
    const initials = (user?.name || 'SPC').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
    ctx.fillStyle = '#0a0a0a'; ctx.font = '900 100px Arial, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(initials, photoX + photoSize / 2, photoY + photoSize / 2 + 6);
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  }
  ctx.restore();
  roundRectPath(ctx, photoX, photoY, photoSize, photoSize, 22);
  ctx.strokeStyle = 'rgba(225,226,10,.4)'; ctx.lineWidth = 2; ctx.stroke();

  const fieldX = photoX + photoSize + 44;
  let fy = photoY + 4;
  ctx.fillStyle = '#7d8085'; ctx.font = '700 12px Arial, sans-serif';
  ctx.fillText('MEMBER NAME', fieldX, fy + 12);
  ctx.fillStyle = '#fff'; ctx.font = '800 34px Arial, sans-serif';
  ctx.fillText(user?.name || '—', fieldX, fy + 50);
  fy += 82;

  ctx.fillStyle = '#7d8085'; ctx.font = '700 12px Arial, sans-serif';
  ctx.fillText('MEMBER NO.', fieldX, fy + 12);
  ctx.fillStyle = '#fff'; ctx.font = '700 19px Arial, sans-serif';
  ctx.fillText((user?.referralCode || (user?.id || '').slice(0, 8) || '—').toUpperCase(), fieldX, fy + 37);
  fy += 60;

  ctx.fillStyle = '#7d8085'; ctx.font = '700 12px Arial, sans-serif';
  ctx.fillText('MEMBER SINCE', fieldX, fy + 12);
  ctx.fillStyle = '#fff'; ctx.font = '700 19px Arial, sans-serif';
  ctx.fillText(memberSince || '2025', fieldX, fy + 37);
  fy += 60;

  // 5-digit PIN gets its own highlighted "badge" strip — sized to fill the space the QR left behind
  const pinBoxW = W - 40 - fieldX, pinBoxH = 130;
  roundRectPath(ctx, fieldX, fy + 8, pinBoxW, pinBoxH, 18);
  ctx.fillStyle = 'rgba(225,226,10,.08)'; ctx.fill();
  roundRectPath(ctx, fieldX, fy + 8, pinBoxW, pinBoxH, 18);
  ctx.strokeStyle = 'rgba(225,226,10,.4)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.fillStyle = '#c7c9a8'; ctx.font = '700 13px Arial, sans-serif';
  ctx.fillText('MEMBER PIN — GIVE TO TEAM TO CHECK IN', fieldX + 22, fy + 36);
  ctx.fillStyle = '#e1e20a'; ctx.font = '900 52px "Courier New", monospace';
  ctx.fillText((user?.memberPin || '—').split('').join('\u2009'), fieldX + 22, fy + 104);

  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.beginPath();
  ctx.moveTo(40, H - 62); ctx.lineTo(W - 40, H - 62); ctx.stroke();
  ctx.fillStyle = '#7d8085'; ctx.font = '600 12px Arial, sans-serif';
  ctx.fillText('This card certifies active membership of Smart Performance Coaching (SPC).', 40, H - 36);
  ctx.fillStyle = '#e1e20a'; ctx.font = '800 13px Arial, sans-serif';
  ctx.fillText('SPCCLUBHOUSE.CO.UK', 40, H - 18);

  ctx.restore();
  roundRectPath(ctx, 1, 1, W - 2, H - 2, R);
  ctx.strokeStyle = 'rgba(225,226,10,.28)'; ctx.lineWidth = 2; ctx.stroke();

  return canvas.toDataURL('image/png');
}

function CopyButton({ value, label, testId, toast, big }) {
  const [copied, setCopied] = useState(false);
  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return; }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand('copy'); document.body.removeChild(ta);
    if (!ok) throw new Error('copy failed');
  };
  const onClick = async () => {
    try {
      await copyText(value || '');
      setCopied(true);
      toast && toast(`${label} copied`);
      setTimeout(() => setCopied(false), 1000);
    } catch { toast && toast('Copy failed — long-press to copy'); }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-[transform,background-color,color,border-color] duration-150 active:scale-95 ${
        big
          ? 'px-3.5 py-2 text-xs font-bold'
          : 'px-3 py-1.5 text-[11px] font-semibold'
      } ${
        copied
          ? 'border-[#e1e20a]/60 bg-[#e1e20a]/15 text-[#e1e20a]'
          : 'border-white/12 bg-white/[0.04] text-[#c4c8cd] hover:bg-white/10 hover:text-white hover:border-white/20'
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function MembershipCard({ user, tier, memberSince, validThru, toast, setMemberPin }) {
  const [downloading, setDownloading] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinConfirming, setPinConfirming] = useState(false);

  const requestSavePin = () => {
    const digits = pinInput.trim();
    if (!/^\d{4}$/.test(digits)) { toast && toast('Enter exactly 4 digits'); return; }
    setPinConfirming(true);
  };

  const confirmSavePin = async () => {
    const digits = pinInput.trim();
    setPinBusy(true);
    const ok = await setMemberPin(digits);
    setPinBusy(false);
    setPinConfirming(false);
    if (ok) setPinInput('');
  };

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const cardURL = await renderLicenseCard({ user, tier, memberSince, validThru });
      const a = document.createElement('a');
      a.href = cardURL;
      a.download = `spc-membership-${user?.name?.replace(/\s+/g, '-').toLowerCase() || 'card'}.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      toast && toast('Membership card saved');
    } catch {
      toast && toast('Could not generate card — try again');
    } finally {
      setDownloading(false);
    }
  };

  const initials = (user?.name || 'SPC').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
  const TierIcon = tierIcon(user?.roleNames);

  return (
    <div
      data-testid="membership-card"
      className="group relative w-full overflow-hidden rounded-2xl border border-white/10 bg-[#111111] p-6 shadow-2xl shadow-black/80 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(225,226,10,0.14)] sm:p-8"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: NOISE_BG }} />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-[#e1e20a]" />
      {/* Faint giant watermark shield — driving-licence style security texture */}
      <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-14 -right-14 h-64 w-64 rotate-[-12deg] object-contain opacity-[0.05] sm:h-72 sm:w-72" />
      {/* Foil shine sweep on hover — premium physical-card feel */}
      <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.07] to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />

      <div className="relative z-10 mb-7 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="SPC" className="h-10 w-auto shrink-0 object-contain drop-shadow-[0_0_6px_rgba(225,226,10,0.4)]" />
          <div>
            <div className="text-sm font-extrabold tracking-wide text-white">SPC CLUBHOUSE</div>
            <div className="text-[10px] font-semibold tracking-[0.15em] text-[#8b8e93]">OFFICIAL MEMBERSHIP CARD</div>
          </div>
        </div>
        <span
          data-testid="membership-tier-badge"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#e1e20a]/50 bg-[#e1e20a]/12 px-5 py-2.5 text-base font-black tracking-wide text-[#e1e20a] shadow-[0_0_24px_rgba(225,226,10,0.28)] sm:text-lg sm:px-6 sm:py-3"
        >
          <TierIcon size={16} className="shrink-0" />
          {((user?.roleNames && user.roleNames.length) ? user.roleNames.join(' · ') : (tier || 'SPC Club')).toUpperCase()}
        </span>
      </div>

      <div className="relative z-10 flex flex-col gap-6 sm:flex-row">
        <div
          data-testid="membership-photo"
          className="h-44 w-44 shrink-0 overflow-hidden rounded-xl border border-white/10 shadow-lg shadow-black/50 sm:h-56 sm:w-56"
        >
          {user?.avatar ? (
            <img src={user.avatar} alt={user?.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[#e1e20a] text-4xl font-black text-black">{initials}</div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div>
            <div className="flex items-center gap-1.5">
              <span className="truncate text-xl font-bold text-white sm:text-2xl">{user?.name}</span>
              <BadgeCheck size={17} className="shrink-0 text-[#e1e20a]" data-testid="membership-verified-badge" />
            </div>
            <div className="text-xs font-medium text-[#8b8e93]">Member since {memberSince || '2025'}</div>
          </div>

          <div className="rounded-xl border border-[#e1e20a]/30 bg-[#e1e20a]/[0.06] px-3.5 py-2.5">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#c7c9a8]">Member PIN{!user?.pinCustomized ? ' (create your own 4-digit PIN)' : ''}</div>
            {user?.pinCustomized ? (
              <>
                <div className="flex flex-wrap items-center gap-2.5">
                  <code data-testid="membership-member-pin" className="font-mono text-2xl font-black tracking-[0.16em] text-[#e1e20a] sm:text-[28px]">
                    {user?.memberPin || '—'}
                  </code>
                  <CopyButton value={user?.memberPin} label="Member PIN" testId="copy-member-id-btn" toast={toast} big />
                </div>
                <div className="mt-1 text-[11px] text-[#7d8085]">Give this PIN to a team member at any session to check in — it's also your referral code</div>
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 opacity-60">
                  <code data-testid="membership-member-pin" className="font-mono text-lg font-black tracking-[0.16em] text-[#e1e20a]">
                    {user?.memberPin || '—'}
                  </code>
                  <span className="text-[10px] font-semibold uppercase text-[#8b8e93]">auto-assigned for now</span>
                </div>
                <div className="mt-3 rounded-2xl border-2 border-[#e1e20a] bg-[#e1e20a]/10 p-3.5 shadow-[0_0_24px_rgba(225,226,10,0.25)]" data-testid="set-pin-cta-block">
                  <div className="flex items-center gap-2 text-sm font-black text-[#e1e20a]">🔑 Pick your own 4-digit PIN</div>
                  <div className="mt-1 text-xs text-white/80">One-time only — easy to remember, and it becomes your referral code too.</div>
                  {!pinConfirming ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2.5">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        placeholder="0000"
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        data-testid="custom-pin-input"
                        className="w-28 rounded-lg border-2 border-[#e1e20a]/60 bg-black/40 px-3 py-2.5 text-center font-mono text-2xl font-black tracking-[0.3em] text-white outline-none focus:border-[#e1e20a]"
                      />
                      <button
                        type="button"
                        onClick={requestSavePin}
                        disabled={pinBusy || pinInput.length !== 4}
                        data-testid="save-custom-pin-btn"
                        className="flex-1 min-w-[160px] rounded-lg bg-[#e1e20a] px-4 py-2.5 text-sm font-black text-black shadow-lg disabled:opacity-30"
                      >
                        SET MY 4-DIGIT PIN
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-lg border border-[#e1e20a]/40 bg-black/30 px-3 py-2.5" data-testid="confirm-pin-panel">
                      <div className="mb-2 text-xs font-semibold text-white">Set <span className="font-mono text-[#e1e20a]">{pinInput}</span> as your PIN &amp; referral code? This can't be changed later.</div>
                      <div className="flex gap-2">
                        <button type="button" onClick={confirmSavePin} disabled={pinBusy} data-testid="confirm-custom-pin-btn" className="flex-1 rounded-lg bg-[#e1e20a] px-3 py-2 text-xs font-black text-black disabled:opacity-40">
                          {pinBusy ? 'Saving…' : 'YES, SET IT'}
                        </button>
                        <button type="button" onClick={() => setPinConfirming(false)} disabled={pinBusy} data-testid="cancel-custom-pin-btn" className="rounded-lg border border-white/20 bg-white/[0.05] px-3 py-2 text-xs font-bold text-white disabled:opacity-40">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-1.5 text-[11px] text-white/60">{pinInput.length}/4 digits entered</div>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#8b8e93]">Referral code</div>
            <div className="flex flex-wrap items-center gap-2">
              <code data-testid="membership-referral-code" className="rounded-lg border border-dashed border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-sm font-bold tracking-wide text-white">
                {user?.referralCode || '—'}
              </code>
              <CopyButton value={user?.referralCode} label="Referral code" testId="copy-referral-btn" toast={toast} />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-white/10 pt-4">
        <span className="max-w-md text-xs text-[#8b8e93]">Show this card at any SPC session — a member of the team will check you in.</span>
        <button
          type="button"
          data-testid="download-card-btn"
          onClick={download}
          disabled={downloading}
          className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-2 text-xs font-bold text-white transition-[transform,background-color,border-color] duration-150 hover:border-[#e1e20a]/40 hover:bg-[#e1e20a]/10 hover:text-[#e1e20a] active:scale-95 disabled:opacity-60"
        >
          <Download size={14} /> {downloading ? 'Preparing…' : 'Download card'}
        </button>
      </div>
    </div>
  );
}
